import React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, MenuIcon, SearchIcon, XIcon } from 'lucide-react';
import { Logo } from '../brand/Logo';
import { Button } from '../ui/Button';
import { SERVICE_LINES } from '../../data/services';
const INSIGHT_LINKS = [{
  to: '/insights',
  label: 'Articles',
  detail: 'Analysis by sector and service line'
}, {
  to: '/resources',
  label: 'Resources',
  detail: 'eBooks, reports and checklists'
}, {
  to: '/assessment',
  label: 'AI readiness assessment',
  detail: 'Score your organisation in four minutes'
}];
export function Header() {
  const [scrolled, setScrolled] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState<'services' | 'insights' | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  React.useEffect(() => {
    setOpenMenu(null);
    setDrawerOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMenu(null);
        setDrawerOpen(false);
        setSearchOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  React.useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  const onSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchOpen(false);
    navigate('/insights');
    setQuery('');
  };
  const triggerClass = (menu: 'services' | 'insights') => `flex items-center gap-1.5 text-[15px] transition-colors ${openMenu === menu ? 'text-gold' : 'text-ink/80 hover:text-ink'}`;
  return <header className={`sticky top-0 z-40 w-full px-3 transition-all duration-200 sm:px-5 ${scrolled ? 'pt-2' : 'pt-4'}`} data-id="element-169" data-mp="2qnmedi22m7dq">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-6 focus:top-6 focus:z-50 focus:rounded-sm focus:bg-gold focus:px-4 focus:py-2 focus:text-charcoal" data-id="element-170" data-mp="2mkpr3e1l5n3g">
        Skip to content
      </a>

      <div className={`mx-auto flex w-full max-w-[1240px] items-center gap-8 rounded-lg border border-line px-5 backdrop-blur-md transition-all duration-200 sm:px-6 ${scrolled ? 'bg-[#0A2445]/92 py-2.5' : 'bg-[#0A2445]/70 py-3.5'}`} data-id="element-171" data-mp="27xei9jwsgh71">
        <Link to="/" aria-label="Cavalla Solution, home" className="shrink-0" data-id="element-172" data-mp="iey35yxzqihg">
          <Logo variant="reversed" data-id="element-173" data-mp="n9lrnxszvcw1" />
        </Link>

        <div ref={navRef} className="hidden items-center gap-8 lg:flex" data-id="element-174" data-mp="9gvssssdadhi">
          <NavLink to="/about" className={({
          isActive
        }) => `text-[15px] transition-colors ${isActive ? 'text-gold' : 'text-ink/80 hover:text-ink'}`} data-id="element-175" data-mp="ef75dba1r23f">
            About us
          </NavLink>

          <div className="relative" data-id="element-176" data-mp="165tpigjoglhb">
            <button type="button" aria-expanded={openMenu === 'services'} aria-haspopup="true" onClick={() => setOpenMenu(openMenu === 'services' ? null : 'services')} className={triggerClass('services')} data-id="element-177" data-mp="s6klef8qxhtp">
              Services
              <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${openMenu === 'services' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-178" data-mp="3hvmmbashy6nx" />
            </button>
            {openMenu === 'services' && <div className="absolute left-0 top-full z-50 mt-5 w-[340px] rounded-md border border-line bg-panel p-2 shadow-2xl" data-id="element-179" data-mp="3ocpo8999n6uw">
                <Link to="/services" className="block rounded-sm px-4 py-2.5 text-[15px] font-medium text-ink hover:bg-mist" data-id="element-180" data-mp="3jffp21w2rgtv">
                  All services
                </Link>
                {SERVICE_LINES.map(line => <Link key={line.id} to={`/services/${line.id}`} className="block px-4 py-2.5 hover:bg-mist" data-id="element-181" data-mp="fvbb7z9282dc">
                    <span className="block text-[15px] font-medium text-ink" data-id="element-182" data-mp="b05qv4vb2vrd">{line.name}</span>
                    <span className="mt-0.5 block text-small text-muted" data-id="element-183" data-mp="61m4528w1fxy">{line.short}</span>
                  </Link>)}
                <Link to="/augmented-intelligence" className="mt-1 block border-t border-line px-4 py-2.5 pt-3.5 text-[15px] text-body hover:bg-mist" data-id="element-184" data-mp="2qp7i766zoiul">
                  Our method
                </Link>
              </div>}
          </div>

          <NavLink to="/cavalla-gov" className={({
          isActive
        }) => `text-[15px] transition-colors ${isActive ? 'text-gold' : 'text-ink/80 hover:text-ink'}`} data-id="element-185" data-mp="2qjg8x9fygzrq">
            Cavalla Gov
          </NavLink>

          <NavLink to="/webinars" className={({
          isActive
        }) => `text-[15px] transition-colors ${isActive ? 'text-gold' : 'text-ink/80 hover:text-ink'}`} data-id="element-186" data-mp="34b4ltey13s9r">
            Webinars
          </NavLink>

          <div className="relative" data-id="element-187" data-mp="3e7zgfmr6l8ir">
            <button type="button" aria-expanded={openMenu === 'insights'} aria-haspopup="true" onClick={() => setOpenMenu(openMenu === 'insights' ? null : 'insights')} className={triggerClass('insights')} data-id="element-188" data-mp="3e6y47eyfifzg">
              Insights
              <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${openMenu === 'insights' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-189" data-mp="16vqqf7guvohf" />
            </button>
            {openMenu === 'insights' && <div className="absolute left-0 top-full z-50 mt-5 w-[340px] rounded-md border border-line bg-panel p-2 shadow-2xl" data-id="element-190" data-mp="1ut650ernt6li">
                {INSIGHT_LINKS.map(link => <Link key={link.to} to={link.to} className="block rounded-sm px-4 py-2.5 hover:bg-mist" data-id="element-191" data-mp="24j9avfjvqf19">
                    <span className="block text-[15px] font-medium text-ink" data-id="element-192" data-mp="1zk67s0ngmivx">{link.label}</span>
                    <span className="mt-0.5 block text-small text-muted" data-id="element-193" data-mp="2djxfjjndv31h">{link.detail}</span>
                  </Link>)}
              </div>}
          </div>
        </div>

        <div className="ml-auto hidden items-center gap-5 lg:flex" data-id="element-194" data-mp="2s6mmu7nvmkif">
          {searchOpen ? <form onSubmit={onSearch} className="flex items-center gap-2" data-id="element-195" data-mp="2x5quuuz2ec76">
              <label htmlFor="site-search" className="sr-only" data-id="element-196" data-mp="2x4xtyamsegwy">
                Search insights and resources
              </label>
              <input id="site-search" type="search" autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder="Search insights" className="w-48 border-b border-white/40 bg-transparent px-1 py-1.5 text-[15px] text-white placeholder:text-white/45" data-id="element-197" data-mp="2o4spa88z9wmh" />
              <button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search" data-id="element-198" data-mp="24gsadr7gyxyp">
                <XIcon className="h-5 w-5 text-white/70" aria-hidden="true" data-id="element-199" data-mp="1qhkb6ybb3pui" />
              </button>
            </form> : <button type="button" onClick={() => setSearchOpen(true)} aria-label="Open search" data-id="element-200" data-mp="1vf297tnscknq">
              <SearchIcon className="h-5 w-5 text-white transition-colors hover:text-gold" aria-hidden="true" data-id="element-201" data-mp="2ax3klpxrikko" />
            </button>}

          <Button to="/contact" size="sm" shape="pill" className="px-5 py-2.5" data-id="element-202" data-mp="25xh8jjto7adg">
            Book a discovery call
          </Button>
          <Link to="/portal/login" className="text-[15px] text-white underline underline-offset-4 transition-colors hover:text-gold" data-id="element-203" data-mp="1wz5bayyatec1">
            Client login
          </Link>
        </div>

        <button type="button" className="ml-auto lg:hidden" aria-expanded={drawerOpen} aria-controls="mobile-nav" aria-label={drawerOpen ? 'Close menu' : 'Open menu'} onClick={() => setDrawerOpen(open => !open)} data-id="element-204" data-mp="1n80udtp2bjek">
          {drawerOpen ? <XIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-205" data-mp="198hufo6enedc" /> : <MenuIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-206" data-mp="14b9cnop65ccm" />}
        </button>
      </div>

      {drawerOpen && <div id="mobile-nav" className="fixed inset-x-0 bottom-0 top-[76px] z-40 overflow-y-auto bg-charcoal lg:hidden" data-id="element-207" data-mp="3fznxivvfh6tf">
          <nav aria-label="Mobile" className="flex flex-col gap-1 px-5 py-6" data-id="element-208" data-mp="3pwiu39854x52">
            <Link to="/about" className="px-1 py-3 text-[17px] text-white" data-id="element-209" data-mp="3txkvnwpj5la7">
              About us
            </Link>
            <div className="my-3 h-px bg-white/15" data-id="element-210" data-mp="2x7v635w0btnt" />
            <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-211" data-mp="325wodollzoy8">Services</p>
            {SERVICE_LINES.map(line => <Link key={line.id} to={`/services/${line.id}`} className="px-1 py-3 text-[17px] text-white" data-id="element-212" data-mp="366p1isxlqdzs">
                {line.name}
              </Link>)}
            <Link to="/augmented-intelligence" className="px-1 py-3 text-[17px] text-white" data-id="element-213" data-mp="2eh410gj1ypf8">
              Our method
            </Link>
            <div className="my-3 h-px bg-white/15" data-id="element-214" data-mp="2igcqvaaz235i" />
            <Link to="/cavalla-gov" className="px-1 py-3 text-[17px] text-white" data-id="element-215" data-mp="20x9cew9vn977">
              Cavalla Gov
            </Link>
            <div className="my-3 h-px bg-white/15" data-id="element-216" data-mp="25uhxb4metrc5" />
            <Link to="/webinars" className="px-1 py-3 text-[17px] text-white" data-id="element-217" data-mp="29xejt6f2m7ld">
              Webinars
            </Link>
            <div className="my-3 h-px bg-white/15" data-id="element-218" data-mp="2ex0x1fuwfnu6" />
            <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-219" data-mp="2euer1knnpge2">Insights</p>
            {INSIGHT_LINKS.map(link => <Link key={link.to} to={link.to} className="px-1 py-3 text-[17px] text-white" data-id="element-220" data-mp="1m721d1m78sgj">
                {link.label}
              </Link>)}
            <div className="my-3 h-px bg-white/15" data-id="element-221" data-mp="1r53l7org8vt2" />
            <Link to="/portal/login" className="px-1 py-3 text-[17px] text-white" data-id="element-222" data-mp="1vyewq377i0rb">
              Client login
            </Link>
            <Button to="/contact" className="mt-5 w-full" size="lg" shape="pill" data-id="element-223" data-mp="yg6gypk2ozjb">
              Book a discovery call
            </Button>
          </nav>
        </div>}
    </header>;
}